import React, { useState, useEffect } from 'react';
import {
  FaSpinner,
  FaMicrophone,
  FaCalendarAlt,
  FaClock,
  FaMapMarkerAlt,
  FaTimes,
  FaEye,
  FaRedo,
} from 'react-icons/fa';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from 'react-router-dom';
import ConferenceSubNav from "../../../Common/ConferenceSubNav";

const RejectedPaidConference = () => {
  const navigate = useNavigate();
  const [rejectedConferences, setRejectedConferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedConference, setSelectedConference] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const stats = {
    rejectedConferences: rejectedConferences.length,
  };

  const fetchRejectedConferences = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/get/rejected/paid-conferences`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const conferences = response.data?.data || [];
      setRejectedConferences(conferences);
      setError(null);
    } catch (err) {
      console.error("Error fetching rejected conferences:", err);
      setError("Failed to load rejected conferences.");
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRejectedConferences();
  }, []);

  const handleViewDetails = (conference) => {
    setSelectedConference(conference);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedConference(null);
    setShowModal(false);
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] pb-24 text-slate-900">
      {/* Premium Compact Header */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-slate-200/50 px-4 py-3">
        <div className="max-w-md mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="px-3 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-600 text-[10px] font-black uppercase tracking-tight hover:bg-slate-100 transition-all whitespace-nowrap shadow-sm"
            >
              ← Go Back
            </button>
            <div className="w-1.5 h-8 bg-gradient-to-b from-rose-500 to-red-600 rounded-full" />
            <div>
              <h1 className="text-lg font-black text-slate-900 leading-tight">Rejected Hub</h1>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider leading-none">Declined Spots</span>
                <div className="w-1 h-1 rounded-full bg-slate-300" />
                <span className="text-[9px] font-black text-rose-600 italic leading-none">{stats.rejectedConferences} Total</span>
              </div>
            </div>
          </div>
          <button
            onClick={fetchRejectedConferences}
            disabled={loading}
            className="w-9 h-9 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400 active:rotate-180 transition-all duration-500"
          >
            <FaRedo className={`text-[10px] ${loading ? "animate-spin text-rose-500" : ""}`} />
          </button>
        </div>
      </div>

      <ConferenceSubNav type="paid" />

      <div className="max-w-5xl mx-auto p-3 space-y-3">
        {loading && (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="w-10 h-10 border-4 border-rose-100 border-t-rose-500 rounded-full animate-spin" />
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Scanning Log...</p>
          </div>
        )}

        {/* Desktop View: Table (Ultra-Compact) */}
        {!loading && !error && (
          <div className="hidden lg:block bg-white rounded-[1.5rem] shadow-sm border border-slate-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50/50 text-slate-400 border-b border-slate-50">
                <tr>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Target Code</th>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Brief Topic</th>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Reason for Decline</th>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Date Out</th>
                  <th className="px-5 py-4 text-center text-[9px] font-black uppercase tracking-widest">Inspect</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {rejectedConferences.map((conference) => (
                  <tr key={conference._id} className="hover:bg-slate-50/30 transition-colors">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center border border-rose-100/50">
                          <FaMicrophone className="text-rose-600 text-[10px]" />
                        </div>
                        <span className="font-black text-slate-400 text-[10px] tabular-nums tracking-tighter">#{conference.conferenceId || "N/A"}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <p className="font-black text-slate-700 text-xs leading-tight line-clamp-1 italic">{conference.conferenceDetails?.topic || "N/A"}</p>
                    </td>
                    <td className="px-5 py-3">
                      <p className="text-[10px] font-bold text-slate-400 line-clamp-1 italic">{conference.rejectNote || "No reason provided"}</p>
                    </td>
                    <td className="px-5 py-3 text-[10px] text-slate-400 font-bold italic tabular-nums">
                      {conference.rejectedAt ? new Date(conference.rejectedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : "N/A"}
                    </td>
                    <td className="px-5 py-3 text-center">
                      <button onClick={() => handleViewDetails(conference)} className="h-8 w-8 bg-slate-900 text-white rounded-lg flex items-center justify-center active:scale-95 transition-all mx-auto"><FaEye className="text-[10px]" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Mobile View: High-Density Record Widgets */}
        {!loading && !error && (
          <div className="lg:hidden max-w-md mx-auto space-y-3">
            {rejectedConferences.length > 0 ? (
              rejectedConferences.map((conference) => (
                <div key={conference._id} className="relative bg-white rounded-[1.75rem] border border-slate-100 p-1 shadow-sm active:scale-[0.99] transition-all overflow-hidden border-b-2 border-b-rose-400/20">
                  <div className="bg-slate-50/40 rounded-[1.6rem] p-3.5 flex flex-col gap-3">
                    {/* Header */}
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-slate-100 p-0.5 shrink-0">
                        <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                          <FaTimes className="text-rose-500 text-sm" />
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-[12px] font-black text-slate-900 leading-tight truncate pr-2 uppercase italic">{conference.conferenceDetails?.topic || "N/A"}</h3>
                        <div className="flex items-center justify-between mt-0.5">
                          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest tabular-nums italic">Code #{conference.conferenceId}</p>
                          <span className="text-[8px] font-black text-rose-600 uppercase">Declined</span>
                        </div>
                      </div>
                    </div>

                    {/* Reason Box */}
                    <div className="bg-rose-50/50 rounded-xl p-2.5 border border-rose-100/30">
                      <p className="text-[8px] font-black text-rose-400 uppercase tracking-widest mb-1">Reason for rejection</p>
                      <p className="text-[10px] font-bold text-rose-900 italic line-clamp-2">"{conference.rejectNote || "No context provided"}"</p>
                    </div>

                    {/* Meta Info */}
                    <div className="flex items-center justify-between px-1">
                      <div className="flex items-center gap-2">
                        <FaCalendarAlt className="text-slate-300 text-[10px]" />
                        <p className="text-[9px] font-black text-slate-400 tabular-nums italic">Terminated: {new Date(conference.rejectedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</p>
                      </div>
                      <button onClick={() => handleViewDetails(conference)} className="h-8 px-4 bg-slate-900 text-white rounded-xl text-[9px] font-black uppercase tracking-widest active:scale-95 transition-all">Inspect Details</button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-24 px-10 text-center">
                <div className="w-16 h-16 rounded-[2rem] bg-white shadow-lg flex items-center justify-center mb-6">
                  <FaTimes className="text-slate-100 text-3xl" />
                </div>
                <h2 className="text-base font-black text-slate-900 tracking-tight">No Rejections</h2>
                <p className="text-[9px] font-bold text-slate-400 mt-2 px-6 uppercase tracking-wider italic leading-relaxed">No declined records found in your mission log.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Decline Record Modal */}
      {showModal && selectedConference && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={handleCloseModal} />
          <div className="relative w-full max-w-sm bg-white rounded-[2.5rem] overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300">
            <div className="bg-rose-600 p-6 text-white text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-white/30 shadow-inner">
                <FaTimes className="text-xl" />
              </div>
              <h3 className="text-xl font-black tracking-tighter leading-tight">Revoked Intel</h3>
              <p className="text-[9px] font-black uppercase tracking-[0.2em] text-rose-100 italic mt-1">Official Decline Record</p>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Mission Topic</p>
                <p className="text-[11px] font-black text-slate-800 leading-tight italic">"{selectedConference.conferenceDetails?.topic || "N/A"}"</p>
              </div>

              <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Rejection Statement</p>
                <p className="text-[11px] font-bold text-slate-600 leading-relaxed italic">"{selectedConference.rejectNote || "No statement provided for this action."}"</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100 text-center">
                  <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Code</p>
                  <p className="text-[10px] font-black text-slate-800 tabular-nums">#{selectedConference.conferenceId}</p>
                </div>
                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100 text-center">
                  <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Time Out</p>
                  <p className="text-[10px] font-black text-slate-800 tabular-nums">{new Date(selectedConference.rejectedAt).toLocaleDateString('en-IN')}</p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-slate-50 px-6 border-t border-slate-100/50">
              <button onClick={handleCloseModal} className="h-12 w-full bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-slate-200">Acknowledge</button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-center" theme="dark" hideProgressBar />
    </div>
  );
};

export default RejectedPaidConference;